import { useState, type ChangeEvent } from 'react';
import {
  Alert, Box, Chip, Divider, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { PasSekcji } from '@/components/PasSekcji';
import { SlupkiRankingowe } from '@/components/wykresy/Wykresy';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { formatCount } from '@/lib/format';
import { useAuth } from '@/lib/auth/AuthContext';
import { StanOdswiezania } from '@/components/StanOdswiezania';
import { useDzisiejszeZamowienia } from '@/lib/useAnalytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * DZISIEJSZE ZAMÓWIENIA — ekran, przy którym stoi się w kuchni.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Restauracja otwierając ten ekran ma NATYCHMIAST wiedzieć:
 *   ile zamówień · ile posiłków ugotować · do ilu szkół jedzie · ile do której ·
 *   czego ile przygotować · które danie do której szkoły.
 *
 * Żadnej z tych odpowiedzi nie wolno chować pod najazdem ani za kliknięciem — ekran bywa
 * używany na tablecie, w hałasie, przy pracy obiema rękami.
 *
 * ┌─ BEZ WYKRESÓW KOŁOWYCH ─────────────────────────────────────────────────────────────────────┐
 * │ Stary panel używa tu pierścieni (`doughnutChartType = 'doughnut'`). Właściciel zdecydował    │
 * │ o zamianie na słupki poziome. Powód jest operacyjny, nie estetyczny: z pierścienia nie da   │
 * │ się odczytać LICZBY SZTUK, a to jedyna rzecz, po którą kucharz tu sięga.                     │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * WYKRES NIGDY NIE ZASTĘPUJE LISTY. Pod każdym rankingiem stoją pełne liczby — słupek pokazuje
 * proporcję, tabela mówi, ile dokładnie.
 */

const DNI_SKROT = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];

function dzisiaj(): string {
  return new Date().toISOString().slice(0, 10);
}

function Kpi({ etykieta, wartosc, opis }: { etykieta: string; wartosc: number; opis?: string }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 140 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{etykieta}</Typography>
      {/* Liczba ma być czytelna z odległości ramienia — to jest ekran roboczy, nie raport. */}
      <Typography variant="h4" sx={{ lineHeight: 1.1 }}>{formatCount(wartosc)}</Typography>
      {opis && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{opis}</Typography>}
    </Stack>
  );
}

export function DzisiejszeZamowieniaPage() {
  const auth = useAuth();
  const [dzien, setDzien] = useState(dzisiaj());

  /*
   * Konto restauracji ma własny zakres i niczego nie wskazuje. Właściciel MUSI wskazać, którą
   * restaurację ogląda — serwer i tak sprawdza to wskazanie względem tożsamości, więc pole
   * poniżej jest wygodą, a nie uprawnieniem.
   */
  // `admin.users.read` ma wyłącznie rola administratora systemu głównego — czyli właściciel.
  // Pytamy o UPRAWNIENIE, a nie o nazwę roli: nazwy ról bywają przemapowywane, uprawnienia nie.
  const czyWlasciciel = auth.has('admin.users.read');
  const [restauracja, setRestauracja] = useState<string>('');

  const wskazana = restauracja.trim() === '' ? undefined : Number(restauracja);
  const zapytanie = useDzisiejszeZamowienia(wskazana, dzien);
  const { data, isLoading, error } = zapytanie;

  const p = data?.podsumowanie;
  const brakWskazania = czyWlasciciel && wskazana === undefined;

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Dzisiejsze zamówienia"
        opis="Co i ile ugotować, i do której szkoły to jedzie."
      />

      <Karta>
        <Stack direction="row" spacing={2} useFlexGap sx={{ p: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField label="Dzień dostawy" type="date" size="small" value={dzien}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDzien(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} />
          {czyWlasciciel && (
            <TextField label="Restauracja (nr)" size="small" sx={{ width: 180 }} value={restauracja}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRestauracja(e.target.value)}
              helperText="Konto właściciela nie ma własnej restauracji" />
          )}
          {/*
            Warunek pyta o SAMO POLE, nie o obecność odpowiedzi.
            `data && data.podstawa.dzien_liczony_wg` wywracało cały ekran, gdy odpowiedź
            przyszła bez `podstawa` — a to jest ekran roboczy kuchni, używany w trakcie
            wydawania posiłków. Brak jednego podpisu nie może zabrać całej listy.
          */}
          {data?.podstawa?.dzien_liczony_wg && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {data.podstawa.dzien_liczony_wg}
            </Typography>
          )}
        </Stack>

        <Stack sx={{ px: 2, pb: 2 }}>
          <StanOdswiezania
            pobranoO={zapytanie.dataUpdatedAt}
            odswiezanie={zapytanie.isFetching}
            bladOdswiezania={zapytanie.isError}
            nieudanychProb={zapytanie.failureCount}
            onOdswiez={() => { void zapytanie.refetch(); }}
          />        </Stack>
      </Karta>

      {brakWskazania && (
        <EmptyState
          title="Wskaż restaurację"
          description="Konto właściciela nie jest przypisane do restauracji, więc trzeba podać, czyj panel oglądasz."
        />
      )}

      {!brakWskazania && isLoading && <LoadingState />}
      {!brakWskazania && error ? <ErrorState error={error} context="Panel restauracji — dzisiejsze zamówienia." /> : null}

      {/*
        ┌─ WARUNEK PYTA O PODSUMOWANIE, NIE O SAMĄ ODPOWIEDŹ ────────────────────────────────┐
        │ Wcześniej stało tu `data &&`, a w środku `p!` — wykrzyknik uciszał TypeScript,     │
        │ ale nie zmieniał tego, co dzieje się w przeglądarce. Odpowiedź bez `podsumowanie`  │
        │ wywracała CAŁY ekran roboczy kuchni w trakcie wydawania posiłków.                  │
        │                                                                                    │
        │ Teraz brak podsumowania znaczy „nie ma czego pokazać", a nie „aplikacja padła".    │
        └────────────────────────────────────────────────────────────────────────────────────┘
      */}
      {data && p && (
        <>
          {/* ── PODSUMOWANIE OPERACYJNE ─────────────────────────────────────────────────── */}
          <Karta>
            <Stack direction="row" spacing={4} useFlexGap sx={{ p: 2, flexWrap: 'wrap' }}>
              <Kpi etykieta="Zamówienia" wartosc={p.zamowien} />
              <Kpi etykieta="Posiłki do przygotowania" wartosc={p.posilkow} opis="suma sztuk" />
              <Kpi etykieta="Szkoły" wartosc={p.szkol} opis="dokąd dziś jedzie" />
            </Stack>
          </Karta>

          {/*
            Pozycje bez daty dostawy NIE są wliczone. Cicho pominięte zamówienie to zamówienie
            niezrealizowane — mówimy o nim wprost, zamiast pozwolić mu zniknąć w zerze.
          */}
          {data.pozycji_bez_daty_dostawy > 0 && (
            <Alert severity="warning">
              <strong>{data.pozycji_bez_daty_dostawy}</strong>{' '}
              {data.pozycji_bez_daty_dostawy === 1 ? 'pozycja nie ma' : 'pozycji nie ma'} zapisanej
              daty dostawy i {data.pozycji_bez_daty_dostawy === 1 ? 'nie została' : 'nie zostały'}{' '}
              wliczone do tego dnia. Nie wiemy, kiedy jadą.
            </Alert>
          )}

          {p.pozycji === 0 && (
            <EmptyState
              title="Brak zamówień na ten dzień"
              description="Na wskazany dzień dostawy nie ma zrealizowanych zamówień. To nie jest błąd — sprawdź inny dzień."
            />
          )}

          {p.pozycji > 0 && (
            <>
              {/* ── ZAMÓWIENIA WG SZKÓŁ ──────────────────────────────────────────────── */}
              <Karta>
                <PasSekcji tytul="Zamówienia wg szkół" wariant="cichy" />
                <Box sx={{ px: 2, pb: 1 }}>
                  <SlupkiRankingowe
                    pozycje={data.wg_szkol.map((s) => ({
                      nazwa: s.szkola, wartosc: s.posilkow, klucz: String(s.szkola_id),
                    }))}
                    format={(v) => `${formatCount(v)} szt.`}
                    maks={12}
                    komunikatPusty="Brak szkół w tym dniu."
                  />
                </Box>

                {/* Wykres pokazuje proporcję. Tabela mówi, ILE DOKŁADNIE — i to ona jest robocza. */}
                <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Zamówienia wg szkół — liczby">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Szkoła</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Zamówienia</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Posiłki</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.wg_szkol.map((s) => (
                        <TableRow key={s.szkola_id ?? s.szkola} hover>
                          <TableCell>{s.szkola}</TableCell>
                          <TableCell align="right">{formatCount(s.zamowien)}</TableCell>
                          <TableCell align="right"><strong>{formatCount(s.posilkow)}</strong></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Karta>

              {/* ── NAJLEPIEJ SPRZEDAJĄCE SIĘ DANIA ──────────────────────────────────── */}
              <Karta>
                <PasSekcji tytul="Ile czego przygotować" wariant="cichy" />
                <Box sx={{ px: 2, pb: 1 }}>
                  <SlupkiRankingowe
                    pozycje={data.wg_dan.map((d) => ({ nazwa: d.danie, wartosc: d.sztuk }))}
                    format={(v) => `${formatCount(v)} szt.`}
                    kolor="var(--seria-2)"
                    maks={15}
                    komunikatPusty="Brak dań w tym dniu."
                  />
                </Box>
              </Karta>

              {/* ── ROZBICIE SZKOŁA → DANIE ──────────────────────────────────────────── */}
              <Karta>
                <PasSekcji tytul="Co do której szkoły" wariant="cichy" />
                <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
                  {/* §11: to NIE ma być schowane pod najazdem — przy tym się pakuje. */}
                  Rozpiska do pakowania. Każda szkoła osobno, z liczbą sztuk każdego dania.
                </Typography>

                <Stack spacing={0} sx={{ p: 2 }}>
                  {data.rozbicie_szkola_danie.map((s, i) => (
                    <Box key={s.szkola_id ?? s.szkola}>
                      {i > 0 && <Divider sx={{ my: 1.5 }} />}
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 0.5 }}>
                        <Typography variant="subtitle1">{s.szkola}</Typography>
                        <Chip size="small" label={`razem ${formatCount(s.razem)} szt.`} />
                      </Stack>
                      <Stack component="ul" spacing={0.25} sx={{ pl: 2, m: 0, listStyle: 'none' }}>
                        {s.dania.map((d) => (
                          <Stack key={d.danie} component="li" direction="row" spacing={1}
                            sx={{ alignItems: 'baseline', justifyContent: 'space-between', maxWidth: 520 }}>
                            <Typography variant="body2">{d.danie}</Typography>
                            <Typography variant="body2"><strong>{formatCount(d.sztuk)}</strong></Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Karta>
            </>
          )}
        </>
      )}
    </Stack>
  );
}

export default DzisiejszeZamowieniaPage;
export { DNI_SKROT };
